import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Btn, Input, Select, Field } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { Plus, X, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { istDateString } from "@/lib/date";

export default function Transfers() {
  const m = useMasters();
  const { user } = useAuth();
  const canWrite = user?.role === "super_admin" || (Array.isArray(user?.permissions)
    ? user.permissions.includes("transfer:write") : ["admin", "manager", "inventory"].includes(user?.role));
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [mode, setMode] = useState("transfer");
  const today = istDateString();
  const emptyForm = () => ({
    transfer_date: today, source_location_id: "", destination_location_id: "",
    technology: "THERMAL", plate_size_id: "", quantity: 0, buyer_name: "", remarks: "",
  });
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => setList((await api.get("/inventory/transfers")).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setReason(""); setForm(emptyForm()); setShow(true); setMode("transfer"); };
  const openEdit = (entry) => {
    const values = emptyForm();
    Object.keys(values).forEach(key => { values[key] = entry[key] ?? values[key]; });
    setEditing(entry); setReason(""); setForm(values); setShow(true); setMode("transfer");
  };
  const close = () => { if (!busy) setShow(false); };

  const submit = async () => {
    if (busy) return;
    if (!Number.isInteger(Number(form.quantity)) || Number(form.quantity) <= 0) { toast.error("Enter a positive whole-number quantity"); return; }
    if (editing && !reason.trim()) { toast.error("Enter a reason for this edit"); return; }
    setBusy(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity) };
      if (editing) {
        await api.put(`/inventory/transfers/${editing.id}`, { ...payload, expected_revision: editing.revision ?? 0, reason: reason.trim() });
      } else if (mode === "sale") {
        await api.post("/inventory/sales_out", { sale_date:payload.transfer_date, source_location_id:payload.source_location_id, technology:payload.technology, plate_size_id:payload.plate_size_id, quantity:payload.quantity, buyer_name:payload.buyer_name, remarks:payload.remarks });
      } else await api.post("/inventory/transfers", payload);
      toast.success(editing ? "Transfer updated, stock corrected" : mode === "sale" ? "Outside sale recorded" : "Transfer complete");
      setShow(false); await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const cancelTransfer = async () => {
    if (busy || !cancelling) return;
    if (!cancelReason.trim()) { toast.error("Enter a cancellation reason"); return; }
    setBusy(true);
    try {
      await api.post(`/inventory/transfers/${cancelling.id}/cancel`, { expected_revision: cancelling.revision ?? 0, reason: cancelReason.trim() });
      toast.success("Transfer cancelled, stock returned to source");
      setCancelling(null); await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="transfers-page">
      <Toaster position="bottom-right" />
      <PageHeader title="Stock Transfers" subtitle="Move stock between locations. Both sides commit together."
        actions={canWrite && <Btn onClick={openNew} data-testid="transfer-new-btn"><Plus size={14} /> New Transfer</Btn>} />
      <div className="p-3 sm:p-6 min-w-0">
        <Card>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] data-table">
            <thead><tr>
              <th className="px-3 py-2 text-left">Transfer No.</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">From</th>
              <th className="px-3 py-2 text-left">To</th>
              <th className="px-3 py-2 text-left">Technology</th>
              <th className="px-3 py-2 text-left">Size</th>
              <th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Actions</th>
            </tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-zinc-500 text-sm">No transfers.</td></tr>}
              {list.map(t => (
                <tr key={t.id}>
                  <td className="px-3 py-2 font-mono font-semibold">{t.transfer_number}</td>
                  <td className="px-3 py-2 font-mono">{t.transfer_date}</td>
                  <td className="px-3 py-2">{nameOf(m.locations, t.source_location_id)}</td>
                  <td className="px-3 py-2">{nameOf(m.locations, t.destination_location_id)}</td>
                  <td className="px-3 py-2 font-mono">{t.technology || "—"}</td>
                  <td className="px-3 py-2 font-mono">{m.plate_sizes.find(x=>x.id===t.plate_size_id)?.canonical || "-"}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{t.quantity}</td>
                  <td className="px-3 py-2"><span className={t.status === "cancelled" ? "text-red-700 font-semibold" : "text-zinc-600"}>{t.status === "cancelled" ? "Cancelled" : "Completed"}</span></td>
                  <td className="px-3 py-2">{canWrite && t.status !== "cancelled" && <div className="flex gap-2"><Btn variant="outline" onClick={()=>openEdit(t)} data-testid={`transfer-edit-${t.id}`}><PencilSimple size={14}/> Edit</Btn><Btn variant="danger" onClick={()=>{setCancelling(t);setCancelReason("");}} data-testid={`transfer-cancel-${t.id}`}>Cancel</Btn></div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      </div>

      {cancelling && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div role="dialog" aria-modal="true" aria-labelledby="cancel-transfer-title" className="bg-white p-5 rounded-sm max-w-lg w-full">
          <h2 id="cancel-transfer-title" className="font-bold text-lg">Cancel {cancelling.transfer_number}?</h2>
          <p className="text-sm text-zinc-600 my-3">This returns {cancelling.quantity} plates from {nameOf(m.locations, cancelling.destination_location_id)} to {nameOf(m.locations, cancelling.source_location_id)}. The entry will stay in the history as cancelled.</p>
          <Field label="Reason*"><Input value={cancelReason} maxLength={500} onChange={e=>setCancelReason(e.target.value)} data-testid="transfer-cancel-reason" /></Field>
          <div className="flex justify-end gap-2 mt-4"><Btn disabled={busy} variant="outline" onClick={()=>setCancelling(null)}>Keep Transfer</Btn><Btn disabled={busy} variant="danger" onClick={cancelTransfer} data-testid="transfer-cancel-confirm">{busy ? "Cancelling…" : "Confirm Cancellation"}</Btn></div>
        </div>
      </div>}

      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={close}>
          <div
            aria-labelledby="transfer-form-title"
            aria-modal="true"
            className="bg-white rounded-sm border border-zinc-200 w-full max-w-lg my-2 sm:my-0"
            data-testid="transfer-form-dialog"
            onClick={e => e.stopPropagation()}
            role="dialog"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div id="transfer-form-title" className="font-heading font-bold">
                {editing
                  ? `Edit ${editing.transfer_number}`
                  : mode === "sale"
                    ? "Sell Outside"
                    : "New Transfer"}
              </div>
              <button disabled={busy} onClick={close} aria-label="Close form"><X size={18} /></button>
            </div>
            <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {!editing && <div className="sm:col-span-2 flex flex-wrap gap-2"><Btn variant={mode==="transfer"?"primary":"outline"} onClick={()=>setMode("transfer")}>Transfer</Btn><Btn variant={mode==="sale"?"primary":"outline"} onClick={()=>setMode("sale")}>Sell Outside</Btn></div>}
              <Field label="Date*"><Input type="date" value={form.transfer_date} onChange={e=>setForm({...form,transfer_date:e.target.value})} /></Field>
              <Field label="Quantity*"><Input type="number" min="1" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})} /></Field>
              <Field label="From Location*">
                <Select value={form.source_location_id} onChange={e=>setForm({...form,source_location_id:e.target.value})}>
                  <option value="">—</option>{m.locations.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>
              {mode === "transfer" && <Field label="To Location*">
                <Select value={form.destination_location_id} onChange={e=>setForm({...form,destination_location_id:e.target.value})}>
                  <option value="">—</option>{m.locations.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>}
              <Field label="Technology*">
                <Select value={form.technology} onChange={e=>setForm({...form,technology:e.target.value})}>
                  <option value="THERMAL">Thermal</option><option value="CTCP">CTCP</option>
                </Select>
              </Field>
              {mode === "sale" && <Field label="Buyer / Party*"><Input value={form.buyer_name} onChange={e=>setForm({...form,buyer_name:e.target.value})} /></Field>}
              <Field label="Plate Size*">
                <Select value={form.plate_size_id} onChange={e=>setForm({...form,plate_size_id:e.target.value})}>
                  <option value="">—</option>{m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
                </Select>
              </Field>
              <div className="sm:col-span-2"><Field label="Remarks"><Input value={form.remarks} onChange={e=>setForm({...form,remarks:e.target.value})} /></Field></div>
            </div>
            {editing && <div className="px-5 pb-4"><Field label="Reason for edit*"><Input value={reason} maxLength={500} onChange={e=>setReason(e.target.value)} data-testid="inventory-edit-reason" /></Field><p className="text-xs text-zinc-500 mt-2">Saving corrects stock and records the change in the audit log.</p></div>}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <Btn disabled={busy} variant="outline" onClick={close}>Close</Btn>
              <Btn disabled={busy} onClick={submit} data-testid="transfer-submit-btn">{busy ? "Saving…" : editing ? "Save Changes" : "Save"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

